import {
  Button,
  Checkbox,
  Form,
  FormInstance,
  Input,
  Radio,
  Select,
  Space,
  message,
} from "antd";
import React, { useEffect, useState } from "react";
import {
  addClientAddress,
  checkPhoneExists,
  fetchClientAlternateContact,
  fetchClientDetails,
  resolveClientAlternative,
} from "../../../apis/client.api";
import { getAddressFromPincode } from "../../../apis/misc.api";
import { backfillClientUserGst, fetchClientUserGsts, verifyClientUserGst } from "../../../apis/user.api";
import { ProcurementMapPicker } from "../../misc/map-picker";
import { usePermissions } from "../../../custom_hooks/permission/permission_hook";
import { INDIAN_STATES, canonicalState } from "../../../constants/indian_states.constants";
interface AddClientAddressFormProps {
  user_id: any;
  fetchAddresses: Function;
  onClientAddAddressDrawerClose: Function;
  /**
   * When true, force is_billing=true and disable the checkbox (used when the
   * caller opened the form specifically to add a billing address).
   */
  billingOnly?: boolean;
  /**
   * When true, force is_billing=false, disable the checkbox, and hide the
   * GST-verify section (used when the caller opened the form specifically
   * to add a shipping address).
   */
  shippingOnly?: boolean;
}
const { Option } = Select;
const { TextArea } = Input;

/**
 * The GST portal's floor field (`flno`) is free text — real GSTINs come back as
 * "1ST FLOOR", "GROUND FLOOR", "G-2". floor_no is an IntegerField, so pull the
 * first run of digits out and drop anything that has none ("GROUND" -> "").
 */
const floorDigits = (value: any) => String(value ?? "").match(/\d+/)?.[0] ?? "";

const AddClientAddressForm = ({
  fetchAddresses,
  onClientAddAddressDrawerClose,
  user_id,
  billingOnly = false,
  shippingOnly = false,
}: AddClientAddressFormProps) => {
  const [form] = Form.useForm();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission("client.update");
  const [alternate_contacts, setAlternateContacts] = useState<Array<any>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectedMapAddress, setSelectedMapAddress] = useState<any>();
  const [gstinInput, setGstinInput] = useState("");
  const [gstVerifying, setGstVerifying] = useState(false);
  const [gstVerifyError, setGstVerifyError] = useState("");
  const [gstPreviewConfirmed, setGstPreviewConfirmed] = useState(false);
  const [savedGsts, setSavedGsts] = useState<Array<any>>([]);

  // A billing address is a bill-to record, never a delivery destination — the
  // backend splits the two on exactly this flag (is_billing=False *is* the
  // shipping set). So the fields that only exist to price and plan a delivery —
  // building type, floor, lift, the map pin — are asked for on shipping
  // addresses only. What the GST portal returns (legal name, address, pincode,
  // city, state) is the authoritative record for a billing address; it cannot
  // tell us the floor or whether there's a lift, and nobody unloads a truck at
  // an invoice.
  const isBillingWatched = Form.useWatch("is_billing", form);
  const isBilling = billingOnly || gstPreviewConfirmed || !!isBillingWatched;

  const fetchAlternateContacts = (user_data: any) => {
    fetchClientAlternateContact(user_id).then((res) => {
      if (res) {
        setAlternateContacts([
          ...res,
          {
            id: user_data?.id,
            contact_person_name: user_data?.f_name,
            contact_person_contact: user_data?.contact,
          },
        ]);
      }
      if (billingOnly && user_data) {
        const currentName = form.getFieldValue("contact_person_name");
        const currentPhone = form.getFieldValue("contact_person_contact");
        form.setFieldsValue({
          contact_person_name: currentName || user_data?.f_name || "",
          contact_person_contact: currentPhone || user_data?.contact || "",
        });
      }
    });
  };

  const onFinish = async (values: any) => {
    // The alternate-contact fields only feed client_alternative; they are not
    // columns on the address. `gender` is the saved-contact picker.
    const {
      alternate_contact_name,
      alternate_contact_phone,
      gender,
      ...updatedValues
    } = values;
    try {
      setSubmitting(true);

      if (!canEdit) {
        message.error("You don't have permission to add an address");
        return;
      }

      // A GST-verified billing address is geocoded from the portal address, and
      // that geocode is allowed to fail — an invoice doesn't need coordinates.
      if (!isBilling && !selectedMapAddress) {
        message.error("Select a location from map");
        return;
      }

      // On a shipping address contact_person_* is the site POC; the alternate
      // contact is asked for separately.
      const client_alternative = isBilling
        ? null
        : await resolveClientAlternative(
            user_id,
            alternate_contact_name,
            alternate_contact_phone
          );

      await addClientAddress(
        {
          ...updatedValues,
          gst_no: updatedValues?.gst_no,
          pan_no: updatedValues?.pan_no,
          ...(client_alternative ? { client_alternative } : {}),
        },
        user_id
      ).then((res) => {
        fetchAddresses(user_id);
        onClientAddAddressDrawerClose();
        form.resetFields();
      });
    } catch (e) {
      message.error("Enter a valid google map link");
    } finally {
      setSubmitting(false);
    }
  };
  const SubmitButton = ({ form }: { form: FormInstance }) => {
    const [submittable, setSubmittable] = React.useState(false);

    // Watch all values
    const values = Form.useWatch([], form);

    React.useEffect(() => {
      form.validateFields({ validateOnly: true }).then(
        () => {
          setSubmittable(true);
        },
        (reason) => {
          setSubmittable(false);
        }
      );
    }, [values]);

    return (
      <Button
        type="primary"
        htmlType="submit"
        disabled={!submittable || !canEdit}
        loading={submitting}
      >
        Submit
      </Button>
    );
  };

  const fetchAllAlternateContacts = () => {
    fetchClientDetails(user_id).then((res) => {
      fetchAlternateContacts(res);
    });
  };

  useEffect(() => {
    fetchAllAlternateContacts();
    if (billingOnly) {
      form.setFieldValue("is_billing", true);
    } else if (shippingOnly) {
      form.setFieldValue("is_billing", false);
    }
    if (!shippingOnly && user_id) {
      fetchClientUserGsts(user_id).then((res) => {
        if (Array.isArray(res)) setSavedGsts(res);
      });
    }
  }, []);

  // Mirrors the storefront modal: a pincode only ever fills city/state, and the
  // country stays pinned to India. It never touches the address label or the
  // address text — those belong to the map pin.
  const setAddressFromPincode = (pincode: any) => {
    if (pincode?.length == 6) {
      getAddressFromPincode(pincode).then((res) => {
        if (res) {
          form.setFieldsValue({
            state: canonicalState(res?.state),
            city: res?.city,
            country: "India",
          });
        } else {
          message.error("Pincode doesn't exist");
        }
      });
    }
  };

  const onContactSelect = async (value: string) => {
    form.setFieldValue('gender', value);
    const contact = alternate_contacts.find((contact) => {
      return contact?.id == value;
    });
    if (contact) {
      form.setFieldsValue({
        alternate_contact_name: contact?.contact_person_name,
        alternate_contact_phone: contact?.contact_person_contact,
      });
      const phone = contact?.contact_person_contact;
      if (!/^[6-9]\d{9}$/gi.test(phone)) {
        message.error("Invalid phone number on this alternate contact. Please update it.");
        form.setFields([{ name: 'alternate_contact_phone', errors: ['Invalid phone number. Please update the alternate contact.'] }]);
      } else {
        const existsInOrg = await checkPhoneExists(phone);
        if (existsInOrg) {
          message.error("This contact already exists in the organisation — pick a different one.");
          form.setFields([{ name: 'alternate_contact_phone', errors: ['This contact already exists in the organisation.'] }]);
          form.setFieldsValue({ gender: undefined, alternate_contact_name: "", alternate_contact_phone: "" });
        }
      }
    } else {
      form.setFieldsValue({
        alternate_contact_name: "",
        alternate_contact_phone: "",
      });
    }
    form.validateFields(['alternate_contact_phone', 'alternate_contact_name']);
  };

  const geocodeAddress = (addressText: string): Promise<any | null> =>
    new Promise((resolve) => {
      if (!addressText || typeof window === "undefined" || !(window as any).google?.maps) {
        resolve(null);
        return;
      }
      try {
        const geocoder = new (window as any).google.maps.Geocoder();
        geocoder.geocode(
          { address: addressText, componentRestrictions: { country: "IN" } },
          (results: any, status: any) => {
            if (status !== "OK" || !results?.[0]) {
              resolve(null);
              return;
            }
            const first = results[0];
            const loc = first.geometry?.location;
            resolve({
              lat: loc?.lat?.() ?? null,
              long: loc?.lng?.() ?? null,
              address: first.formatted_address || addressText,
              google_map_address:
                loc?.lat && loc?.lng
                  ? `https://google.com/maps?q=${loc.lat()},${loc.lng()}`
                  : "",
              id: first.place_id,
            });
          }
        );
      } catch {
        resolve(null);
      }
    });

  const onVerifyGstin = async (explicitGstin?: string) => {
    const trimmed = (explicitGstin ?? gstinInput ?? "").trim().toUpperCase();
    setGstVerifyError("");
    if (trimmed.length !== 15) {
      setGstVerifyError("GSTIN must be exactly 15 characters");
      return;
    }
    setGstVerifying(true);
    const result = await verifyClientUserGst(trimmed);
    if (!result.ok) {
      setGstVerifying(false);
      setGstVerifyError(result.error);
      return;
    }
    const preview: any = result.preview;
    const gstin = preview.gst_number;
    const companyName = preview.legal_name || "";

    const geocoded = preview.address ? await geocodeAddress(preview.address) : null;
    setGstVerifying(false);

    form.setFieldsValue({
      is_billing: true,
      name: companyName || form.getFieldValue("name"),
      gst_no: gstin,
      pan_no: gstin ? gstin.slice(2, 12) : form.getFieldValue("pan_no"),
      address: preview.address || form.getFieldValue("address"),
      pincode: preview.pincode || form.getFieldValue("pincode"),
      city: preview.city || form.getFieldValue("city"),
      state: canonicalState(preview.state) || form.getFieldValue("state"),
      country: "India",
      flat_no: preview.plot_no || form.getFieldValue("flat_no"),
      floor_no: floorDigits(preview.floor_no) || form.getFieldValue("floor_no"),
      ...(geocoded
        ? {
            lat: geocoded.lat,
            long: geocoded.long,
            google_map_address: geocoded.google_map_address,
          }
        : {}),
    });

    if (geocoded?.lat && geocoded?.long) {
      setSelectedMapAddress({
        ...geocoded,
        name: companyName,
        city: preview.city,
        state: preview.state,
        country: preview.country,
        pincode: preview.pincode,
      });
    }

    setGstinInput("");
    setGstPreviewConfirmed(true);
  };

  return (
    <Form
      form={form}
      name="validateOnly"
      layout="vertical"
      autoComplete="off"
      initialValues={{ country: "India", address_type: "apartment" }}
      onFinish={onFinish}
    >
      {!shippingOnly && (
      <div
        style={{
          border: "1px solid #E5E7EB",
          borderRadius: 8,
          padding: 12,
          marginBottom: 16,
          background: "#F9FAFB",
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          Verify GST for B2B billing
        </div>
        <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 8 }}>
          Verify a GSTIN to make this a billing address and prefill
          the details.
        </div>
        {!gstPreviewConfirmed && savedGsts.length > 0 && (
          <Select
            style={{ width: "100%", marginBottom: 8 }}
            placeholder="Pick a saved GST"
            allowClear
            loading={gstVerifying}
            disabled={gstVerifying}
            onChange={async (val) => {
              if (!val) return;
              const entry = savedGsts.find((g) => g?.gst_number === val);
              if (entry && !entry.gst_address && user_id) {
                setGstVerifying(true);
                const result = await backfillClientUserGst(user_id, val);
                if (!result.ok) {
                  setGstVerifying(false);
                  const res = await fetchClientUserGsts(user_id);
                  if (Array.isArray(res)) setSavedGsts(res);
                  setGstVerifyError(
                    result.error ||
                      `${val} is not a valid GSTIN and has been removed.`,
                  );
                  return;
                }
                setGstVerifying(false);
                setSavedGsts((prev) =>
                  prev.map((g) => (g.id === result.entry.id ? result.entry : g)),
                );
              }
              onVerifyGstin(val);
            }}
          >
            {savedGsts.map((g) => (
              <Option value={g?.gst_number} key={g?.id ?? g?.gst_number}>
                {g?.gst_number}
                {g?.label ? ` — ${g.label}` : ""}
                {g?.gst_company_name ? ` (${g.gst_company_name})` : ""}
              </Option>
            ))}
          </Select>
        )}
        {!gstPreviewConfirmed && (
          <Space.Compact style={{ width: "100%" }}>
            <Input
              placeholder="15-char GSTIN"
              value={gstinInput}
              maxLength={15}
              disabled={gstVerifying}
              onChange={(e) => {
                setGstinInput(e.target.value.toUpperCase());
                setGstVerifyError("");
              }}
              onPressEnter={() => onVerifyGstin()}
            />
            <Button
              type="primary"
              loading={gstVerifying}
              onClick={() => onVerifyGstin()}
            >
              Verify
            </Button>
          </Space.Compact>
        )}
        {gstVerifyError && (
          <div style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>
            {gstVerifyError}
          </div>
        )}
      </div>
      )}
      <Form.Item
        label=""
        name="is_billing"
        valuePropName="checked"
        hidden={shippingOnly || billingOnly}
      >
        <Checkbox disabled={!!gstPreviewConfirmed}>Billing address</Checkbox>
      </Form.Item>
      <Form.Item
        name="name"
        rules={[
          {
            required: true,
          },
        ]}
        label="Address label"
      >
        <Input />
      </Form.Item>
      {isBilling ? (
        <div className="row">
          <div className="col-6">
            <Form.Item
              name="contact_person_name"
              rules={[{ required: true }]}
              label="Contact person"
            >
              <Input />
            </Form.Item>
          </div>
          <div className="col-6">
            <Form.Item
              name="contact_person_contact"
              rules={[
                {
                  required: true,
                  pattern: /^[6-9]\d{9}$/,
                  message: "Enter a valid Phone",
                },
              ]}
              label="Contact number"
            >
              <Input />
            </Form.Item>
          </div>
        </div>
      ) : (
        <>
          {/* Who receives the delivery on site. Typed in, never prefilled: the
              client account holder is often not the person at the site. */}
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Site POC (Receiver's Details)
          </div>
          <div className="row">
            <div className="col-6">
              <Form.Item
                name="contact_person_name"
                rules={[{ required: true, message: "Enter the site POC's name" }]}
                label="Name"
              >
                <Input />
              </Form.Item>
            </div>
            <div className="col-6">
              <Form.Item
                name="contact_person_contact"
                rules={[
                  { required: true, message: "Enter the site POC's phone" },
                  { pattern: /^[6-9]\d{9}$/, message: "Enter a valid Phone" },
                ]}
                label="Phone"
              >
                <Input maxLength={10} />
              </Form.Item>
            </div>
          </div>

          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            Alternate Contact
          </div>
          {alternate_contacts?.length > 0 && (
            <Form.Item
              name="gender"
              label="Use an existing alternate contact (optional)"
            >
              <Select
                placeholder="Pick a saved contact to prefill"
                onChange={onContactSelect}
                allowClear
              >
                {alternate_contacts?.map((contact) => {
                  return (
                    <Option value={contact?.id} key={contact?.id}>
                      {contact?.contact_person_name} ({contact?.contact_person_contact})
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          )}
          <div className="row">
            <div className="col-6">
              <Form.Item
                name="alternate_contact_name"
                rules={[{ required: true }]}
                label="Alternate Contact name"
              >
                <Input />
              </Form.Item>
            </div>
            <div className="col-6">
              <Form.Item
                name="alternate_contact_phone"
                dependencies={["contact_person_contact"]}
                rules={[
                  {
                    required: true,
                    pattern: /^[6-9]\d{9}$/,
                    message: "Enter a valid Phone",
                  },
                  ({ getFieldValue }) => ({
                    validator: (_, value) =>
                      value && value === getFieldValue("contact_person_contact")
                        ? Promise.reject(
                            new Error("Must differ from the site POC's phone")
                          )
                        : Promise.resolve(),
                  }),
                ]}
                label="Alternate Contact phone"
              >
                <Input maxLength={10} />
              </Form.Item>
            </div>
          </div>
        </>
      )}

      <ProcurementMapPicker
        onPick={(selectedMapAddress) => {
          setSelectedMapAddress(selectedMapAddress);
          Object.entries(selectedMapAddress).forEach(([key, value]) => {
            form.setFieldValue(key, value);
          });
          form.setFieldValue("country", "India");
          // Google hands back its own spelling ("Karnataka", "TS", "Orissa") —
          // canonicalise it or the state Select has nothing to match on.
          form.setFieldValue(
            "state",
            canonicalState(selectedMapAddress?.state) || selectedMapAddress?.state
          );
          if (!selectedMapAddress?.google_map_address) {
            form.setFieldValue(
              "google_map_address",
              `https://www.google.com/maps?q=${selectedMapAddress?.lat},${selectedMapAddress?.long}`
            );
          }
        }}
        openInModal={window.innerWidth > 768}
        pickedAddress={selectedMapAddress}
      />
      <Form.Item
        rules={[
          {
            pattern: /\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/g,
            message: "Enter a valid GST Number",
          },
        ]}
        name="gst_no"
        label="GST Number (required for B2B invoice)"
      >
        <Input disabled placeholder="Verify a GSTIN above to fill" />
      </Form.Item>
      <Form.Item
        name="pan_no"
        label="PAN No."
        rules={[
          {
            pattern: /[A-Z]{5}\d{4}[A-Z]{1}/g,
            message: "Enter a valid PAN Number",
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Space>
        <Form.Item
          rules={[
            {
              required: !isBilling,
            },
          ]}
          label="Building Type"
          name={"address_type"}
          hidden={isBilling}
          style={{ minWidth: 140, flexShrink: 0 }}
        >
          <Select placeholder="Select building type">
            <Option value="villa">Villa</Option>
            <Option value="apartment">Apartment</Option>
            <Option value="office">Office</Option>
            <Option value="restaurant">Restaurant</Option>
            <Option value="factory">Factory</Option>
            <Option value="other">Other</Option>
          </Select>
        </Form.Item>
        {/* On a billing address these two are whatever the GST portal returned
            (plot_no / floor_no), and the portal often returns neither — so they
            are shown but never gate the save. */}
        <Form.Item
          name="flat_no"
          rules={[
            {
              required: !isBilling,
            },
          ]}
          label="Tower / Flat No. / Plot No."
        >
          <Input />
        </Form.Item>
        {/* On a shipping address this is always asked for, whatever the building
            type — the floor drives the unloading charge, so a villa still has to
            say "0". */}
        <Form.Item
          name="floor_no"
          rules={[
            {
              required: !isBilling,
            },
            {
              pattern: /^\d+$/,
              message: "Enter a valid floor number",
            },
          ]}
          label="Floor No."
        >
          <Input
            inputMode="numeric"
            onChange={(e) =>
              form.setFieldValue(
                "floor_no",
                e.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, "")
              )
            }
          />
        </Form.Item>
      </Space>
      <Form.Item
        name="lift_available"
        label="Lift Available"
        hidden={isBilling}
        rules={[
          { required: !isBilling, message: "Select if a lift is available" },
        ]}
      >
        <Radio.Group optionType="button">
          <Radio.Button value={true}>Yes</Radio.Button>
          <Radio.Button value={false}>No</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item hidden name="lat">
        <Input />
      </Form.Item>
      <Form.Item hidden name="long">
        <Input />
      </Form.Item>
      {/* Read-only on purpose, exactly as on the storefront: the address text and
          the map pin are only ever allowed to travel together. Change it by
          picking a new location above. */}
      <Form.Item
        name="address"
        rules={[
          {
            required: true,
            message: "Pick a location on the map to fill the address",
          },
        ]}
        label="Full Address"
        extra="Filled from the map pin — pick a location above to change it"
      >
        <TextArea rows={4} readOnly />
      </Form.Item>
      <Form.Item
        name="pincode"
        rules={[
          {
            required: true,
            pattern: /^[1-9]\d{5}$/gi,
            message: "Enter a valid pincode",
          },
        ]}
        label="Pincode"
      >
        <Input
          onChange={(e) => {
            setAddressFromPincode(e.target.value);
          }}
        />
      </Form.Item>
      <Space>
        <Form.Item
          name="city"
          rules={[{ required: true }]}
          label="City"
          >
          <Input />
        </Form.Item>
        <Form.Item
          name="state"
          rules={[
            {
              validator: (_, value) =>
                canonicalState(value)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error("Select a valid state from the list")
                    ),
            },
          ]}
          label="State"
          >
          <Select
            showSearch
            placeholder="Select state"
            optionFilterProp="children"
            style={{ minWidth: 180 }}
          >
            {INDIAN_STATES.map((name) => (
              <Option key={name} value={name}>
                {name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="country"
          rules={[{ required: true }]}
          label="Country"
          >
          <Input readOnly />
        </Form.Item>
      </Space>
      <Form.Item>
        <Space>
          <SubmitButton form={form} />
          {/* <Button htmlType="reset">Reset</Button> */}
        </Space>
      </Form.Item>
    </Form>
  );
};

export default AddClientAddressForm;
